import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { EmailService } from '../communications/email.service';
import { environment } from '../../../environments/environment';
import { BehaviorSubject, Observable, of, throwError, catchError, map, tap, take, filter, switchMap, defer, EMPTY } from 'rxjs';
import { PaginationService } from '../pagination.service';
import { GoogleSheetsBaseService } from '../google-sheets/google-sheets-base.service';
import moment from 'moment';
import { EmailSheetsService } from '../google-sheets/email-sheets.service';
import { Assessment, AssessmentScoreChoice, AssessmentSummary, ScoreOption } from '../../objects/assessment';

/**
 * A service dedicated to managing assessments. It handles fetching and writing
 * assessment records to the main database and performs related business logic
 * like querying and score calculation.
 */
@Injectable({
  providedIn: 'root'
})
export class AssessmentsService extends GoogleSheetsBaseService {
  private emailService = inject(EmailService);
  private paginationService = inject(PaginationService);
  private emailSheetsService = inject(EmailSheetsService);

  private allAssessmentsSubject = new BehaviorSubject<any[]>([]);
  private isCacheRefreshedSubject = new BehaviorSubject<boolean>(false);
  public isCacheRefreshed$ = this.isCacheRefreshedSubject.asObservable();
  private cacheRequested = false;

  /**
   * Every assessment (full records). Loads on first subscription, not when the service is
   * created: the contract page injects this service for its Assessments tab, and the old
   * constructor fetch pulled every assessment on every contract view.
   */
  public allAssessments$: Observable<any[]> = defer(() => {
    this.ensureCacheLoaded();
    return this.allAssessmentsSubject.asObservable();
  });

  /** v2 API base (…/api/{org}/assessments). Only set for orgs moved to v2; the rest keep the
   *  legacy routes and behave exactly as before. */
  private readonly v2Base: string | undefined = (environment as any).URL_ASSESSMENTS;

  get usesV2(): boolean {
    return !!this.v2Base;
  }

  constructor() {
    super();
  }

  private ensureCacheLoaded(): void {
    if (this.cacheRequested) return;
    this.cacheRequested = true;
    this.refreshAllAssessments();
  }

  /** The full cache, once it has loaded. */
  private loadedAssessments$(): Observable<any[]> {
    this.ensureCacheLoaded();
    return this.isCacheRefreshed$.pipe(
      filter(done => done),
      take(1),
      switchMap(() => this.allAssessmentsSubject.pipe(take(1)))
    );
  }

  // ========================
  // v2 API (orgs with URL_ASSESSMENTS)
  // Errors are passed through as HttpErrorResponse; use describeError() for the message.
  // ========================

  /** List rows for the Assessments grid (GET /summary). */
  getSummaries(): Observable<AssessmentSummary[]> {
    return this.http.get<any>(`${this.v2Base}/summary`).pipe(map(r => r?.data || []));
  }

  /** One full record, flattened (assessmentData + assessmentNum). */
  getAssessment(assessmentNum: string): Observable<Assessment> {
    return this.http.get<any>(`${this.v2Base}/item/${encodeURIComponent(assessmentNum)}`)
      .pipe(map(r => this.flatten(r?.data)));
  }

  /** Create. The server assigns assessmentNum and revision. */
  createAssessment(assessment: Assessment): Observable<Assessment> {
    const assessmentData = this.stripServerFields(assessment);
    delete assessmentData.assessmentNum;
    return this.http.post<any>(`${this.v2Base}/item`, { assessmentData })
      .pipe(map(r => this.afterWrite(this.flatten(r?.data))));
  }

  /**
   * Full replace. `assessment` must be the COMPLETE record (load it, change fields, send it
   * back) — anything left out is deleted. 409 REVISION_CONFLICT if someone saved in between.
   */
  replaceAssessment(assessment: Assessment, expectedRevision: number): Observable<Assessment> {
    const num = assessment.assessmentNum as string;
    const assessmentData = { ...this.stripServerFields(assessment), assessmentNum: num };
    return this.http.put<any>(`${this.v2Base}/item/${encodeURIComponent(num)}`, { assessmentData, expectedRevision })
      .pipe(map(r => this.afterWrite(this.flatten(r?.data))));
  }

  /** Administrators only (403 otherwise). Hard delete. */
  deleteAssessment(assessmentNum: string): Observable<void> {
    return this.http.delete<any>(`${this.v2Base}/item/${encodeURIComponent(assessmentNum)}`).pipe(
      map(() => undefined),
      tap(() => this.allAssessmentsSubject.next(
        this.allAssessmentsSubject.value.filter(a => (a.assessmentNum || a.assessmentData?.assessmentNum) !== assessmentNum)
      ))
    );
  }

  /** Plain-English message for a failed v2 call. */
  describeError(err: unknown): string {
    const e = err as HttpErrorResponse;
    const body = e?.error || {};
    if (e?.status === 409 && body.code === 'REVISION_CONFLICT') {
      return 'This assessment was changed by someone else. Reload it to see their changes, then make yours again.';
    }
    if (e?.status === 403) {
      return 'Your role does not allow this. Editors and Administrators can create and edit assessments; only Administrators can delete them.';
    }
    if (e?.status === 404) return 'This assessment no longer exists.';
    if (e?.status === 401) return 'Your session has expired. Please sign in again.';
    if (e?.status === 0 || (e?.status ?? 0) >= 500) return 'The server could not be reached. Please try again in a few minutes.';
    return body.message || 'Something went wrong. Please try again.';
  }

  /** Server-owned fields must not be sent back. */
  private stripServerFields(a: Assessment): Assessment {
    const { revision, createdAt, createdBy, updatedAt, updatedBy, ...rest } = a;
    return { ...rest };
  }

  /** v2 records carry assessmentData plus legacy top-level copies; read assessmentData only. */
  private flatten(record: any): Assessment {
    if (!record) return record;
    const data = record.assessmentData || record;
    return { ...data, assessmentNum: data.assessmentNum || record.assessmentNum };
  }

  /** Keep the full cache (if anything loaded it) in step with a write. */
  private afterWrite(saved: Assessment): Assessment {
    if (this.cacheRequested && saved?.assessmentNum) {
      const rest = this.allAssessmentsSubject.value.filter(a =>
        (a.assessmentNum || a.assessmentData?.assessmentNum) !== saved.assessmentNum);
      this.allAssessmentsSubject.next([{ assessmentNum: saved.assessmentNum, assessmentData: saved }, ...rest]);
    }
    return saved;
  }

  // ========================
  // Lookup by entity
  // ========================

  getAssessmentByNum(assessmentNum: string): Observable<Assessment | null> {
    if (this.usesV2) {
      return this.getAssessment(assessmentNum).pipe(catchError(() => of(null)));
    }
    return this.loadedAssessments$().pipe(
      map(assessments => {
        const found = assessments.find(a =>
          a.assessmentNum === assessmentNum ||
          a.assessmentData?.assessmentNum === assessmentNum
        );
        return found ? (found.assessmentData || found) : null;
      })
    );
  }

  getAssessmentsByEntityId(entityId: string): Observable<Assessment[]> {
    if (this.usesV2) {
      return this.http.get<any>(`${this.v2Base}/by-entity/${encodeURIComponent(entityId)}`)
        .pipe(map(r => (r?.data || []).map((rec: any) => this.flatten(rec))));
    }
    return this.loadedAssessments$().pipe(
      map(assessments => assessments
        .filter(a => {
          const data = a.assessmentData || a;
          return data.entityId === entityId;
        })
        .map(a => a.assessmentData || a)
      )
    );
  }

  // ========================
  // Google Sheets Data
  // ========================

  getAssessmentTypes(): Observable<string[]> {
    return this.getSheetColumnData('assessments', 'C2', 'C50');
  }

  getAssessmentStatuses(): Observable<string[]> {
    return this.getSheetColumnData('assessments', 'D2', 'D50');
  }

  getAssessmentQuestions(): Observable<string[]> {
    return this.getSheetColumnData('assessments', 'E2', 'E50');
  }

  getRequiredActions(): Observable<string[]> {
    return this.getSheetColumnData('assessments', 'B2', 'B50');
  }

  getAttestation(): Observable<string[]> {
    return this.getSheetColumnData('assessments', 'A2', 'A2');
  }

  getScoreOptions(): Observable<ScoreOption[]> {
    return this.getSheetData('assessments', 'F2', 'G10').pipe(
      map(values => {
        if (!values || values.length < 2) return [];
        const scores = values[0] || [];
        const labels = values[1] || [];
        return scores.map((score: string, i: number) => ({
          value: score,
          label: labels[i] || score
        }));
      })
    );
  }

  /**
   * Questions for an assessment type. Same rule as the legacy SRH app: the type's position in
   * assessments!C2:C10 picks the column (1st → E, 2nd → J, 3rd → K, 4th → N). Unlike legacy,
   * blank cells are dropped.
   */
  getQuestionsForType(type: string, types: string[]): Observable<string[]> {
    const columns = ['E', 'J', 'K', 'N'];
    const index = types.findIndex(t => t === type);
    const col = columns[index] || 'E';
    return this.getSheetColumnData('assessments', `${col}2`, `${col}20`).pipe(
      map(cells => (cells || []).map(c => (c || '').trim()).filter(c => c))
    );
  }

  /** Score choices from assessments!F2:F10, e.g. "3 - Very Good" → { score: 3 }. "NA …" → 0. */
  getScoreChoices(): Observable<AssessmentScoreChoice[]> {
    return this.getSheetColumnData('assessments', 'F2', 'F10').pipe(
      map(cells => (cells || [])
        .map(c => (c || '').trim())
        .filter(c => c)
        .map(description => {
          const m = description.match(/^(\d+)/);
          return { score: m ? parseInt(m[1], 10) : 0, description };
        }))
    );
  }

  /** Evaluation instructions (HTML) from the assessmentsinstructions tab. Display only. */
  getEvaluationInstructions(): Observable<string> {
    return this.getSheetColumnData('assessmentsinstructions', 'A2', 'A2').pipe(map(c => c?.[0] || ''));
  }

  /** Total = plain sum of numeric scores (NA counts 0). Same as the legacy SRH app. */
  sumScores(questionsAndAnswers: { score: number | string }[] | undefined): number {
    return (questionsAndAnswers || []).reduce((sum, qa) => {
      const n = Number(qa.score);
      return sum + (isNaN(n) ? 0 : n);
    }, 0);
  }

  calculateAverageScore(questionsAndAnswers: { score: string }[]): number {
    if (!questionsAndAnswers || questionsAndAnswers.length === 0) return 0;
    const numericScores = questionsAndAnswers
      .map(qa => parseFloat(qa.score))
      .filter(s => !isNaN(s));
    if (numericScores.length === 0) return 0;
    return numericScores.reduce((sum, s) => sum + s, 0) / numericScores.length;
  }

  /**
   * Refreshes all assessments by calling the paginated API and updating the subject.
   */
  refreshAllAssessments(): void {
    const getPageOfAssessments = (lastEvaluatedKey: any = null): Observable<any> => {
      const url = environment.assessmentsGetPageUrl;
      let params: any = { limit: '100' };
      if (lastEvaluatedKey) {
        params.lastKey = JSON.stringify(lastEvaluatedKey);
      }

      return this.http.get(url, { headers: { 'Content-Type': 'application/json' }, params }).pipe(
        map((response: any) => ({
          Items: response.data || [],
          lastEvaluatedKey: response.lastKey || null
        })),
        catchError(error => {
          console.error('Error fetching assessments page:', error);
          return of({ Items: [], lastEvaluatedKey: null });
        })
      );
    };

    this.paginationService.getAllPaginatedData(getPageOfAssessments).pipe(
      map(assessments => assessments.map((assessment: any) => {
        const totalScore = this.calculateTotalScore(assessment.assessmentData);
        return {
          ...assessment,
          assessmentData: {
            ...assessment.assessmentData,
            totalScore: totalScore
          }
        };
      })),
      tap(finalAssessments => {
        // console.log('Successfully fetched and processed all assessments:', finalAssessments);
      }),
      catchError(error => {
        console.error('Error in assessment data pipeline:', error);
        this.allAssessmentsSubject.next([]);
        return EMPTY;
      })
    ).subscribe(assessments => {
      this.allAssessmentsSubject.next(assessments);
      this.isCacheRefreshedSubject.next(true);
    });
  }

  writeAssessment(assessmentData: any): Observable<any> {
    const url = environment.assessmentsWriteFullUrl;
    const body = {
      assessmentNum: assessmentData.assessmentNum,
      assessmentData: assessmentData
    };
    const options = {
      headers: { 'Content-Type': 'application/json' }
    };
    return this.http.put(url, body, options).pipe(
      catchError(error => {
        console.error('Error writing assessment:', error);
        return throwError(() => new Error('Could not write assessment.'));
      })
    );
  }

  queryAssessmentsField(field: string, fieldValue: string): Observable<any[]> {
    return this.allAssessments$.pipe(
      map(assessments => assessments.filter(assessment => {
        let userId = null;
        switch (field) {
          case 'owner':
          case 'assessor':
          case 'finalReviewer':
            userId = assessment[field]?.email || assessment.assessmentData[field]?.email;
            if (userId === fieldValue) {
              return true;
            }
            break;
          default:
            if (assessment[field] === fieldValue || assessment.assessmentData[field] === fieldValue) {
              return true;
            }
            break;
        }
        return false;
      }))
    );
  }

  queryAssessmentsList(fieldList: string, fieldValue: string): Observable<any> {
    const url = environment.assessmentsQueryListUrl;
    const options = {
      headers: { 'Content-Type': 'application/json' },
      params: { [fieldList]: fieldValue }
    };
    return this.http.get(url, options).pipe(
      catchError(error => {
        console.error('Error querying assessments list:', error);
        return throwError(() => new Error('Could not query assessments list.'));
      })
    );
  }

  calculateTotalScore(assessmentObject: any): number {
    let totalScore = 0;
    if (assessmentObject && assessmentObject.questionsAndAnswers) {
      for (const question of assessmentObject.questionsAndAnswers) {
        if (typeof question.score === 'number') {
          totalScore += question.score;
        }
      }
    }
    return totalScore;
  }

  assessmentSavedEmailTriggers(assessmentData: any, associatedContract: any, associatedParty: any): void {
    // This method now only contains the email logic and no Google Sheets calls.
    const getEmailContent = (templateName: string): Observable<any> => {
      // Assuming this method exists in a new dedicated GoogleSheets service.
      return this.emailService.getEmailTemplate(templateName).pipe(
        map((res: any) => res.values[0])
      );
    };

    const buildEmailDetails = (): string => {
      let details = `<b>Assessment Name:</b> ${assessmentData.name}<br><br>`;
      if (associatedContract) {
        details += `<b>Assessment Contract Name:</b> ${associatedContract.nameOfContract}<br><br>`;
      }
      if (associatedParty) {
        details += `<b>Assessment Party:</b> ${associatedParty.name}<br><br>`;
      }
      if (assessmentData.criticalDate) {
        const dueDateFriendlyString = moment(assessmentData.criticalDate).format('MM/DD/YYYY');
        details += `<b>Due Date:</b> ${dueDateFriendlyString}<br><br>`;
      }
      return details;
    };

    const emailRecipients = {
      owner: assessmentData.owner?.email || assessmentData.owner,
      reviewer: assessmentData.finalReviewer?.email || assessmentData.finalReviewer
    };

    const contractDetailsForEmail = buildEmailDetails();
    let emailObservable$: Observable<any> | null = null;
    let recipient: string | null = null;

    switch (assessmentData.status.toLowerCase()) {
      case 'initial assessment completed':
      case 'initial assessment completed - review required':
      case 'completed':
      case 'reassigned - pending completion':
        recipient = emailRecipients.reviewer;
        emailObservable$ = this.emailSheetsService.getAssessmentAssignedToAssessorsEmail();
        break;
      case 'review required':
      case 'in remediation - action plan pending':
      case 'evaluation completed - ready for final reviewer':
        recipient = emailRecipients.reviewer;
        emailObservable$ = this.emailSheetsService.getAssessmentReadyForFinalReviewEmail();
        break;
      case 'review completed':
      case 'action plan completed':
      case 'cap completed - ready for final reviewer':
        recipient = emailRecipients.owner;
        emailObservable$ = this.emailSheetsService.getAssessmentFinalReviewCompletedEmail();
        break;
      default:
        console.warn('No email trigger for this assessment status.');
        return;
    }

    if (emailObservable$ && recipient) {
      emailObservable$.subscribe({
        next: emailBody => {
          this.emailService.sendEmail(recipient, assessmentData.name, emailBody).subscribe({
            // next: () => console.log('Email sent successfully!'),
            error: err => console.error('Error sending email:', err)
          });
        },
        error: err => console.error('Error fetching email content:', err)
      });
    }
  }
}
